import React from 'react';

import PageContainer from '../../components/layout/page-container/PageContainer';
import { useTranslation } from 'react-i18next';
import { useStyles } from './AdminDashboard.styles';
import theme from '../../styles/theme';
import { Paths } from '../../shared/paths';
import AppTabs from '../../components/designe/app-tabs/AppTabs';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAccess } from '../../hooks/useAccess';
import Others from './others/Others';
import Sync from './sync/Sync';
import Agreements from './agreements/Agreements';
import Notifications from './notifications/Notifications';
import ExerciseManagement from './exercise-management/ExerciseManagement';
import LanguageManagement from './language-management/LanguageManagement';
import AppConfiguration from './app-configuration/AppConfiguration';
import Analytics from './analytics/Analytics';

const AdminDashboard = (props) => {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);

  const { loading, access } = useAccess();
  const hasAccess = access?.adminPanels?.canViewAdminPanel;

  if (!loading && !hasAccess) return <Navigate to={Paths.root} />;

  if (loading) {
    //handle it
    return null;
  }

  const viewAccess = {
    exerciseManagement:
      (access?.exerciseProperties?.canView ?? false) ||
      (access?.exerciseOptions?.canView ?? false) ||
      (access?.exerciseLoadFormulas?.canView ?? false),
    languageManagement:
      (access?.languages?.canView ?? false) ||
      (access?.translations?.canView ?? false) ||
      (access?.aiPrompts?.canView ?? false),
    appConfiguration:
      (access?.bugReports?.canViewType ?? false) ||
      (access?.appVariables?.canView ?? false) ||
      (access?.appFeatures?.canView ?? false) ||
      (access?.trainingActivityTypes?.canView ?? false),
    others: access?.cache?.canResetCache ?? false,
    syncRules: access?.syncRules?.canView ?? hasAccess ?? false,
    sync:
      access?.syncRules?.canView ??
      access?.adminPanels?.canViewAdminPanel ??
      false,
    agreements: access?.agreements?.canView ?? false,
    notifications: access?.notifications?.canView ?? false,
    analytics: access?.analytics?.canView ?? false,
  };

  function getTabsConfig() {
    const tabsConfig = [];

    if (viewAccess.exerciseManagement) {
      tabsConfig.push({
        label: t('common.exercises'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.exerciseManagement}`,
      });
    }

    if (viewAccess.languageManagement) {
      tabsConfig.push({
        label: t('adminDashboard.languages'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.languageManagement}`,
      });
    }

    if (viewAccess.appConfiguration) {
      tabsConfig.push({
        label: t('adminDashboard.appConfiguration'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.appConfiguration}`,
      });
    }

    if (viewAccess.sync) {
      tabsConfig.push({
        label: t('adminDashboard.sync.tab'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.sync}`,
      });
    }

    if (viewAccess.agreements) {
      tabsConfig.push({
        label: t('adminDashboard.agreements'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.agreements}`,
      });
    }

    if (viewAccess.notifications) {
      tabsConfig.push({
        label: t('adminDashboard.notifications.tab'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.notifications}`,
      });
    }

    if (viewAccess.analytics) {
      tabsConfig.push({
        label: t('adminDashboard.analytics.tab'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.analytics}`,
      });
    }

    if (viewAccess.others) {
      tabsConfig.push({
        label: t('adminDashboard.others'),
        path: `${Paths.adminDashboard.root}${Paths.adminDashboard.others}`,
      });
    }

    return tabsConfig;
  }

  const tabsConfig = getTabsConfig();
  const firstTabPath = tabsConfig[0]?.path;

  return (
    <PageContainer title={t('adminDashboard.adminDashboard')}>
      <AppTabs config={tabsConfig} />
      <div className={classes.tabsContent}>
        <Routes>
          {viewAccess.exerciseManagement && (
            <Route
              path={`${Paths.adminDashboard.exerciseManagement}/*`}
              element={<ExerciseManagement />}
            />
          )}
          {viewAccess.languageManagement && (
            <Route
              path={`${Paths.adminDashboard.languageManagement}/*`}
              element={<LanguageManagement />}
            />
          )}
          {viewAccess.appConfiguration && (
            <Route
              path={`${Paths.adminDashboard.appConfiguration}/*`}
              element={<AppConfiguration />}
            />
          )}
          {viewAccess.sync && (
            <Route path={`${Paths.adminDashboard.sync}/*`} element={<Sync />} />
          )}
          {viewAccess.agreements && (
            <Route
              path={`${Paths.adminDashboard.agreements}/*`}
              element={<Agreements />}
            />
          )}
          {viewAccess.notifications && (
            <Route
              path={`${Paths.adminDashboard.notifications}/*`}
              element={<Notifications />}
            />
          )}
          {viewAccess.analytics && (
            <Route
              path={`${Paths.adminDashboard.analytics}/*`}
              element={<Analytics />}
            />
          )}
          {viewAccess.others && (
            <Route path={Paths.adminDashboard.others} element={<Others />} />
          )}
          <Route
            path={'/'}
            element={
              firstTabPath ? (
                <Navigate to={firstTabPath} replace />
              ) : (
                <Navigate to={Paths.root} replace />
              )
            }
          />
        </Routes>
      </div>
    </PageContainer>
  );
};

export default AdminDashboard;
